import {
  ComputeItem,
  ComputeNode,
  ProposalComputeRecord,
  ProposalId,
} from '@shared/models/pipeline/compute.model';
import {
  Project,
  ExpressionValue,
  Baseline,
  IElement,
  IElementID,
  IProductElement,
  OneOfElements,
  OneOfPropertyElements,
  ProjectMarketEstimates,
} from '@shared/models/project.model';
import {
  IElementExpressionProperty,
  IElementProperty,
  ElementPropertyName,
} from '@shared/models/element-property.model';
import { IElementQuantityExpressionProperty } from '@shared/models/element-quantities.model';
import { Results } from '@shared/models/unit.model';
import {
  buildElementPathIndex,
  getElementByPath,
  getElementPathFromIndices,
  RelativePathIndexMap,
} from './batch-update.helpers';
import {
  appendElementToPath,
  ElementPath,
  flattenElements,
  forEachElement,
  isBuildingVersionElement,
  isOneOfPropertyElements,
} from '../recursive-element.helpers';
import {
  getElementAndQuantityProperties,
  getElementProperties,
  getCount,
  hasCount,
  isElementExpressionProperty,
  isInheritanceSupported,
} from '../element-property.helpers';
import { isElementQuantityExpressionProperty } from '../element-quantity.helpers';
import { resolveNodesRelaxed } from './dependency-graph.helpers';
import { getVariablesInExpression } from '../mathjs';
import {
  createExpression,
  isExpressionValue,
} from '../expression-factory.helpers';
import { errorHandledSolve } from '../expression-solving.helpers';
import {
  ExpressionVariables,
  defaultVariables,
  getElementExpressionVariables,
} from '../expression-variables.helpers';
import { IDataTable } from '@shared/models/data-table.model';
import {
  getProjectMeta,
  updateInheritedPropertyFallbacks,
} from '../project.helpers';
import { getLast } from '../array.helpers';
import { multiplyConversionFactors } from '../conversion.helpers';
import {
  getProductElementConversionFactors,
  hasCarbonLevelOverride,
  isGenericProductElement,
} from '../product-element.helpers';
import {
  getProductConservativeFactor,
  NEUTRAL_CONSERVATIVE_FACTOR,
  withConservativeCarbon,
} from '../conservative-carbon.helpers';
import { ProductRecord } from '@shared/models/product.model';
import { genericProductsLookup } from '@shared/generic-products';
import { getProposalsInVersion } from '../proposal.helpers';
import { IProposal } from '@shared/models/proposals.model';
import { isTemporaryElementId } from '../element.helpers';
import { projectGuards } from '../guards/project-guards.helpers';
import {
  applyElementCategoryProperties,
  getElementCategoryId,
} from '../element-category.helpers';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import { syncDataTableProperties } from '../data-tables/data-table-pipeline.helpers';
import { autoUpdateElementQuantity } from '../auto-quantity.helpers';
import {
  type ComputedVariable,
  type ComputedVariableContext,
  heatDemandRecordVariable,
  ahuVariablesVariable,
} from './computed-variables';
import { GLOBAL_VAR_PREFIX, NODE_ID } from './node-ids.helpers';
import { generateVersionHash } from './version-hash.helpers';
import { getBuildingDesignFromProjectOrBuilding } from '../design/design.helpers';
import { designToProjectExpressionVariablesMigration } from '../design/expressions/design-to-project-expression-variables.migration';
import { getDesignExpressionVariables } from '../design/expressions/design-expression-variables.helpers';
import { AHU_VARIABLE_NAME } from '@shared/templates/categories/air-handling-unit/air-handling-unit.model';
import { EnvironmentHourData } from '../energy/weather.models';
import { getEnergyExpressionVariables } from '../energy/energy-expression-variables.helpers';
import {
  getEnergyActivitiesFromDesign,
  getHeatedAreaFromEnergyActivities,
} from '../energy/energy-activity.helpers';
import { profiler } from './pipeline-profiler';
import { resolveVersionForProposal } from '../actions/resolve-version-for-proposal.helpers';
import { withLegacyMetadataExpressionFields } from '../design/legacy-metadata-expression-fields.helpers';

/**
 * Version-level parent variables for compute (design-backed with legacy meta overlays).
 */
export const getBaseParentVariablesForCompute = (
  project: Project,
  _products: ProductRecord,
): ExpressionVariables => {
  const meta = getProjectMeta(project);
  const design = getBuildingDesignFromProjectOrBuilding(project);
  return withLegacyMetadataExpressionFields(
    meta,
    designToProjectExpressionVariablesMigration(
      getDesignExpressionVariables(design),
    ) as ExpressionVariables,
  );
};

/** Return type from {@link collectComputeNodes} */
export type CollectedComputeData = {
  nodes: ComputeNode<number>[];
  /** Elements structurally enriched during compute (category, data tables, quantities) */
  enrichedElements: Map<string, IElement>;
};

enum NodeType {
  Property = 'property',
  ProductElementCount = 'count',

  // Later
  Results = 'results',
}

type PropertyNodeDescriptor = {
  type: NodeType.Property;
  elementId: IElementID;
  parentId?: IElementID;
  name: string;
};

type ProductElementCountNodeDescriptor = {
  type: NodeType.ProductElementCount;
  elementId: IElementID;
  parentId?: IElementID;
};

type NodeDescriptor =
  PropertyNodeDescriptor | ProductElementCountNodeDescriptor;

/** Map from node id to descriptor; used to resolve variable names without parsing id strings */
type NodeDescriptorMap = Map<string, NodeDescriptor>;

/**
 * Intermediate representation of a compute node — captures structure and data
 * but defers closure creation so the same blueprint can be instantiated with
 * different per-proposal {@link ExpressionVariables}.
 */
type NodeBlueprint = {
  id: string;
  elementId: string;
  dependencies: string[];
  /** When set, closure base vars are looked up from elementVarsMap instead of top-level parentVariables */
  varsElementId?: string;
} & (
  | {
      kind: 'inheritance';
      sourceNodeId: string | undefined;
      /**
       * Resolved value of fallbackCount (or count) on the property itself, used when
       * `sourceNodeId` is undefined (no matching ancestor). Mirrors the legacy
       * pipeline behaviour where the local fallback wins instead of returning 0.
       */
      fallbackResolved?: number;
    }
  | {
      kind: 'expression';
      countExp: ExpressionValue;
      min?: number;
      max?: number;
    }
);

const nodeIdFromDescriptor = (d: NodeDescriptor): string =>
  d.type === NodeType.Property
    ? NODE_ID.property(d.elementId, d.name)
    : NODE_ID.productCount(d.elementId);

/** Get variable name for expression resolution from a dependency node id (property nodes and global vars) */
const getVariableNameFromDescriptor = (
  descriptorMap: NodeDescriptorMap,
  nodeId: string,
): string | undefined => {
  if (nodeId.startsWith(GLOBAL_VAR_PREFIX)) {
    const after = nodeId.slice(GLOBAL_VAR_PREFIX.length);
    // Per-element global vars use "varName::elementId"; expression expects varName only
    const idx = after.indexOf('::');
    return idx >= 0 ? after.slice(0, idx) : after;
  }
  const d = descriptorMap.get(nodeId);
  return d?.type === NodeType.Property ? d.name : undefined;
};

/** Elements after path root (building version); empty when the path has no element segments. */
const pathElementsTail = (path: ElementPath): IElement[] => {
  if (path.length < 2) return [];
  const [, ...elements] = path;
  return elements;
};

/**
 * Find an expression or quantity property on an element/version that can provide `varName`.
 */
const findExpressionPropertyProvider = (
  host: OneOfElements,
  varName: string,
): IElementProperty | IElementQuantityExpressionProperty | undefined =>
  getElementAndQuantityProperties(host).find(
    (p) =>
      (isElementExpressionProperty(p) ||
        isElementQuantityExpressionProperty(p)) &&
      p.name === varName &&
      (hasCount(p) ||
        (isInheritanceSupported(p as IElementExpressionProperty) &&
          !hasCount(p))),
  );

/**
 * Resolve a variable name to a node id in path scope (path[1..] = elements; first defining element wins).
 * Multiple elements can define the same property name; the closest in path order wins (shadowing).
 * Version-level properties on path[0] are visible to the whole tree unless shadowed by a descendant.
 * @param varName - Variable name from expression
 * @param path - Element path (path[0] = version, path[1..] = elements)
 * @param parentVariables - Global/project variables (resolve to global:var:name)
 * @returns Node id that provides the variable, or undefined
 */
const resolveVariableToNodeId = (
  varName: string,
  path: ElementPath,
  // parentVariables: Partial<ExpressionVariables>,
): string | undefined => {
  // Path is root-to-leaf; iterate reverse so nearest ancestor with same property/quantity name wins
  const elementsInPath = pathElementsTail(path);
  for (let i = elementsInPath.length - 1; i >= 0; i--) {
    const element = elementsInPath[i];
    if (!projectGuards.is.element(element)) continue;
    const prop = findExpressionPropertyProvider(element, varName);
    if (prop) return NODE_ID.property(element.id, prop.name);
  }

  // Version-level properties (path[0]) — e.g. installationer_utbyte on the version row
  const versionRoot = path[0];
  if (isBuildingVersionElement(versionRoot)) {
    const versionProp = findExpressionPropertyProvider(versionRoot, varName);
    if (versionProp) {
      return NODE_ID.property(versionRoot.id, versionProp.name);
    }
  }

  // Per-AHU vars (e.g. AHU_VARIABLE_NAME.FAN_ENERGY_CONSUMPTION) resolved from parent AHU node
  if (elementsInPath.length >= 2) {
    const parent = elementsInPath[elementsInPath.length - 2];
    if (
      projectGuards.is.element(parent) &&
      getElementCategoryId(parent) === ElementCategoryID.AirHandlingUnit &&
      varName === AHU_VARIABLE_NAME.FAN_ENERGY_CONSUMPTION
    ) {
      return `${GLOBAL_VAR_PREFIX}${varName}::${parent.id}`;
    }
  }

  return `${GLOBAL_VAR_PREFIX}${varName}`;
};

/** True if prop is an expression-like property (element property or quantity) with resolvable value */
const isExpressionLikeProperty = (
  prop: IElementProperty | IElementQuantityExpressionProperty,
): prop is IElementExpressionProperty | IElementQuantityExpressionProperty =>
  isElementExpressionProperty(prop) ||
  isElementQuantityExpressionProperty(prop);

/**
 * Collect node blueprints from element properties and element.quantity.
 * Uses {@link ElementProjectPathMap} from {@link buildElementPathIndex} to resolve both current and
 * original (pre-enrichment) element from version for dependency resolution.
 *
 * Returns {@link NodeBlueprint}s (structure only, no closures) so the same blueprints
 * can be instantiated with different per-proposal variables.
 */
const collectPropertyBlueprints = (
  element: OneOfPropertyElements,
  path: ElementPath,
  version: Baseline,
  pathIndex: RelativePathIndexMap,
  descriptorMap: NodeDescriptorMap,
): NodeBlueprint[] => {
  const indices = pathIndex.get(element.id);
  const originalFromVersion =
    indices !== undefined ? getElementByPath(version, indices) : undefined;
  const originalElement =
    originalFromVersion && projectGuards.is.element(originalFromVersion)
      ? originalFromVersion
      : undefined;

  const pathWithElement = [...path, element] as ElementPath;
  const pathForAncestors: ElementPath =
    indices && indices.length >= 1
      ? getElementPathFromIndices(version, indices.slice(0, -1))
      : path;
  const pathForResolution: ElementPath = indices
    ? getElementPathFromIndices(version, indices)
    : pathWithElement;
  const blueprints: NodeBlueprint[] = [];

  const enrichedProps = getElementAndQuantityProperties(element);
  const originalProps = originalElement
    ? getElementAndQuantityProperties(originalElement)
    : [];
  const propByName = new Map<
    string,
    IElementProperty | IElementQuantityExpressionProperty
  >();
  for (const p of enrichedProps) propByName.set(p.name, p);
  for (const p of originalProps) {
    if (!propByName.has(p.name)) propByName.set(p.name, p);
  }
  const properties = [...propByName.values()];

  for (const prop of properties) {
    // TODO: Should  probably handle select and boolean property inheritance here as well
    if (!isExpressionLikeProperty(prop)) continue;

    const count = getCount(prop);

    // Property/quantity with inheritFallback and no explicit count: depend on nearest ancestor with same name
    if (!hasCount(prop) && isInheritanceSupported(prop)) {
      const sourceNodeId = resolveVariableToNodeId(prop.name, pathForAncestors);
      const currentNodeId = NODE_ID.property(element.id, prop.name);
      const dependencies = sourceNodeId ? [sourceNodeId] : [];

      const descriptor: PropertyNodeDescriptor = {
        type: NodeType.Property,
        elementId: element.id,
        name: prop.name,
      };
      descriptorMap.set(currentNodeId, descriptor);

      // Capture local fallback so we don't collapse to 0 when no ancestor source exists.
      const fallbackResolved = prop.fallbackCount?.resolved;

      blueprints.push({
        kind: 'inheritance',
        id: currentNodeId,
        elementId: element.id,
        dependencies,
        sourceNodeId,
        fallbackResolved,
      });
      continue;
    }

    if (!count) continue;

    const expression = count.expression ?? '';
    const varNames = getVariablesInExpression(expression);
    const dependencies: string[] = [];

    const currentNodeId = NODE_ID.property(element.id, prop.name);
    for (const varName of varNames) {
      let nodeId = resolveVariableToNodeId(varName, pathForResolution);
      // When enriched, if var resolved to global, try ancestors-only path so parent/ancestor props are found
      if (
        originalElement &&
        pathForAncestors.length >= 2 &&
        (nodeId === undefined || nodeId.startsWith(GLOBAL_VAR_PREFIX))
      ) {
        const fromAncestors = resolveVariableToNodeId(
          varName,
          pathForAncestors,
        );
        if (
          fromAncestors !== undefined &&
          !fromAncestors.startsWith(GLOBAL_VAR_PREFIX)
        ) {
          nodeId = fromAncestors;
        }
      }
      if (nodeId !== undefined && nodeId !== currentNodeId) {
        dependencies.push(nodeId);
      }
    }

    const descriptor: PropertyNodeDescriptor = {
      type: NodeType.Property,
      elementId: element.id,
      name: prop.name,
    };
    const nodeId = nodeIdFromDescriptor(descriptor);
    descriptorMap.set(nodeId, descriptor);

    // Normalise literal count to ExpressionValue for blueprint storage
    const countExp = isExpressionValue(count) ? count : createExpression(count);

    blueprints.push({
      kind: 'expression',
      id: nodeId,
      elementId: element.id,
      dependencies,
      countExp,
      min: prop.min,
      max: prop.max,
      // Per-element vars (parent chain, id, product/category vars) are required so
      // scope-aware math.js helpers like getParentValue / getSbefLifetime work.
      varsElementId: element.id,
    });
  }

  return blueprints;
};

/**
 * Path for resolving product-count dependencies: use compute-enriched parent so
 * quantities like `mass` (added by autoUpdate during enrichment) are found (DEV-3354).
 */
const pathForProductCountDependencyResolution = (
  path: ElementPath,
  enrichedElementMap?: Map<string, IElement>,
): ElementPath => {
  const parent = getLast(path);
  if (!parent || !projectGuards.is.element(parent) || !enrichedElementMap) {
    return path;
  }
  const enrichedParent = enrichedElementMap.get(parent.id);
  if (!enrichedParent || path.length < 2) return path;
  const root = path[0];
  if (!root) return path;
  const ancestors = path.slice(1, -1) as IElement[];
  const enrichedPath: ElementPath = [root, ...ancestors, enrichedParent];
  return enrichedPath;
};

/**
 * Resolve a product-count variable to a compute node id.
 * Falls back to {@link descriptorMap} when path lookup only finds a global id but a
 * property node was already registered (e.g. mass from merged original + enriched props).
 */
const resolveProductCountVariableToNodeId = (
  varName: string,
  path: ElementPath,
  descriptorMap: NodeDescriptorMap,
  enrichedElementMap?: Map<string, IElement>,
): string | undefined => {
  const pathForResolution = pathForProductCountDependencyResolution(
    path,
    enrichedElementMap,
  );
  const fromPath = resolveVariableToNodeId(varName, pathForResolution);
  if (fromPath && !fromPath.startsWith(GLOBAL_VAR_PREFIX)) return fromPath;

  const elementsInPath =
    pathForResolution.length >= 2
      ? (pathForResolution.slice(1) as IElement[])
      : [];
  for (let i = elementsInPath.length - 1; i >= 0; i--) {
    const element = elementsInPath[i];
    if (!projectGuards.is.element(element)) continue;
    const nodeId = NODE_ID.property(element.id, varName);
    if (descriptorMap.has(nodeId)) return nodeId;
  }

  return fromPath;
};

/**
 * Collect blueprint for a ProductElement's count.
 * Registers the node's descriptor in descriptorMap. Variables are resolved in path scope.
 */
const collectProductCountBlueprint = (
  element: IProductElement,
  path: ElementPath,
  descriptorMap: NodeDescriptorMap,
  enrichedElementMap?: Map<string, IElement>,
): NodeBlueprint | null => {
  const count = element.count;
  if (!count?.expression) return null;

  const varNames = getVariablesInExpression(count.expression);
  const dependencies: string[] = [];

  for (const varName of varNames) {
    const nodeId = resolveProductCountVariableToNodeId(
      varName,
      path,
      descriptorMap,
      enrichedElementMap,
    );
    if (nodeId !== undefined) {
      dependencies.push(nodeId);
    }
  }

  const descriptor: ProductElementCountNodeDescriptor = {
    type: NodeType.ProductElementCount,
    elementId: element.id,
  };
  const nodeId = nodeIdFromDescriptor(descriptor);
  descriptorMap.set(nodeId, descriptor);

  // Product count uses container element vars (quantities + product density), not the product row.
  const container = getLast(path);
  const varsElementId =
    container && projectGuards.is.element(container)
      ? container.id
      : element.id;

  // Drop stale value/resolved on product count — solve must use dependency graph (DEV-3354).
  const countExp = isExpressionValue(count)
    ? createExpression(count.expression ?? '0')
    : createExpression(count);

  return {
    kind: 'expression',
    id: nodeId,
    elementId: element.id,
    dependencies,
    countExp,
    varsElementId,
  };
};

/**
 * Get elements to compute for a version (used by generateProposalComputeRecord).
 * Always returns ALL elements — deactivated elements still need their
 * expressions resolved. Proposal-based filtering for aggregation is done
 * in buildResultsRecordFromProposalComputeRecord (version-level rollup).
 */
const getComputeElements = (version: Baseline): OneOfElements[] => {
  return flattenElements(version);
};

/**
 * Collect child elements that were added by structural enrichment but don't exist
 * in the original version tree (e.g., product elements from category definitions).
 */
const collectNewChildElements = (
  baseElements: OneOfElements[],
  enrichedElements?: Map<string, IElement>,
): OneOfElements[] => {
  if (!enrichedElements?.size) return [];

  const existingIds = new Set(baseElements.map((e) => e.id));
  const additional: OneOfElements[] = [];

  for (const enrichedEl of enrichedElements.values()) {
    for (const child of enrichedEl.elements) {
      if (!existingIds.has(child.id)) {
        additional.push(child);
        existingIds.add(child.id);
      }
    }
  }

  return additional;
};

/**
 * Uses compute-time enriched product children when a parent category rewrites an
 * existing child count expression, e.g. reused content reducing the original material.
 */
const getEffectiveProductElementForCompute = (
  element: IProductElement,
  path: ElementPath,
  enrichedElements: Map<string, IElement>,
): IProductElement => {
  const parent = getLast(path);
  if (!parent || !projectGuards.is.element(parent)) return element;

  const enrichedParent = enrichedElements.get(parent.id);
  const enrichedChild = enrichedParent?.elements.find(
    (child): child is IProductElement =>
      projectGuards.is.productElement(child) && child.id === element.id,
  );

  return enrichedChild ?? element;
};

const computedVariables: ComputedVariable[] = [
  heatDemandRecordVariable,
  ahuVariablesVariable,
];

/**
 * Run computed variables before rest of dependency resolution: ensure nodes exist,
 * then replace any dependency on a computed variable's node id with the deps returned by getDependencies.
 */
const applyComputedVariables = (
  ctx: ComputedVariableContext,
): ComputeNode<number>[] => {
  let nodes = ctx.nodes;
  for (const cv of computedVariables) {
    cv.ensureNodes?.(ctx);
    const expandedDeps = cv.getDependencies(ctx);
    const nodeId = `${GLOBAL_VAR_PREFIX}${cv.name}`;
    nodes = nodes.map((node) => {
      if (!node.dependencies.includes(nodeId)) return node;
      const depsSet = new Set(node.dependencies.filter((id) => id !== nodeId));
      for (const id of expandedDeps) depsSet.add(id);
      return { ...node, dependencies: [...depsSet] };
    });
    ctx.nodes = nodes;
  }
  return nodes;
};

/**
 * Check if an element should be included in compute scope for the given proposal.
 * Unlike isElementActiveInProposal, this does NOT exclude deactivated/deleted elements.
 * Deactivated elements still need computed results — they are excluded from rollups instead.
 */
const isElementInProposalScope = (
  _proposal: IProposal | undefined,
  _path: OneOfElements[],
  _element: OneOfElements,
): boolean => true;

/**
 * Collect all compute nodes from a building version.
 * Traverses the tree so property lookup can include inherited vars from ancestors.
 *
 * When {@link productRecord} is provided, per-element expression variables are built
 * during traversal (including product-derived vars like density, densityArray, etc.).
 * Without it, only the base {@link parentVariables} are used for all nodes.
 *
 * Structural enrichment is applied in-memory during traversal:
 *  1. {@link updateInheritedPropertyFallbacks} — propagate parent variable values
 *  2. {@link applyElementCategoryProperties} — add/remove properties from category
 *  3. {@link syncDataTableProperties} — sync values from connected data tables
 *  4. {@link autoUpdateElementQuantity} — setup auto-fallback expressions
 *
 * Enriched elements are returned alongside nodes for later application in the enrich phase.
 *
 * When {@link proposal} is set, the traversal uses {@link resolveVersionForProposal} so
 * category templates (child product slots, conditional properties) match stored proposal actions.
 */
export const collectComputeNodes = (
  version: Baseline,
  parentVariables: ExpressionVariables = defaultVariables,
  proposal?: IProposal,
  productRecord?: ProductRecord,
  dataTables: IDataTable[] = [],
  project?: Project,
  weatherHours?: EnvironmentHourData[],
): CollectedComputeData => {
  profiler.time('collectComputeNodes');
  profiler.count('collectComputeNodes:calls');
  // Category child templates (e.g. heating product slots) read element.properties. Proposal
  // choices live on actions until merged — resolve so enrichment matches the active proposal.
  const traversalVersion =
    proposal != null
      ? resolveVersionForProposal(version, proposal).version
      : version;

  // Single traversal: build scope and blueprints in one pass
  const elementsInScope: OneOfElements[] = [];
  const elementIdsInScope = new Set<string>();
  const descriptorMap: NodeDescriptorMap = new Map();
  const blueprints: NodeBlueprint[] = [];

  profiler.time('collectComputeNodes:buildPathIndex');
  const pathIndex = buildElementPathIndex(traversalVersion);
  profiler.timeEnd('collectComputeNodes:buildPathIndex');

  const elementVarsMap = new Map<string, ExpressionVariables>();
  const enrichedElementMap = new Map<string, IElement>();

  forEachElement(traversalVersion, (element, path) => {
    profiler.count('collectComputeNodes:elementsVisited');
    profiler.time('collectComputeNodes:scopeFiltering');
    const inScope = isElementInProposalScope(proposal, path, element);
    profiler.timeEnd('collectComputeNodes:scopeFiltering');
    if (!inScope) return;

    elementsInScope.push(element);
    elementIdsInScope.add(element.id);

    profiler.time('collectComputeNodes:resolveVars');
    const nodeVars = resolveElementVariables(
      element,
      path,
      parentVariables,
      elementVarsMap,
      productRecord,
      dataTables,
    );
    profiler.timeEnd('collectComputeNodes:resolveVars');

    if (isOneOfPropertyElements(element)) {
      profiler.time('collectComputeNodes:enrichment');
      const enrichedElement =
        productRecord && projectGuards.is.element(element)
          ? (profiler.count('enrichElementForCompute:calls'),
            enrichElementForCompute(element, path, nodeVars, dataTables))
          : element;

      if (
        enrichedElement !== element &&
        projectGuards.is.element(element) &&
        projectGuards.is.element(enrichedElement)
      ) {
        resolveElementVariables(
          enrichedElement,
          path,
          parentVariables,
          elementVarsMap,
          productRecord,
          dataTables,
        );
        enrichedElementMap.set(element.id, enrichedElement);
      }
      profiler.timeEnd('collectComputeNodes:enrichment');

      profiler.time('collectComputeNodes:nodeCollection');
      blueprints.push(
        ...collectPropertyBlueprints(
          enrichedElement,
          path,
          traversalVersion,
          pathIndex,
          descriptorMap,
        ),
      );

      if (
        projectGuards.is.element(element) &&
        projectGuards.is.element(enrichedElement)
      ) {
        collectNewProductElementBlueprints(
          element,
          enrichedElement,
          path,
          descriptorMap,
          elementIdsInScope,
          blueprints,
          enrichedElementMap,
        );
      }
      profiler.timeEnd('collectComputeNodes:nodeCollection');
    } else if (projectGuards.is.productElement(element)) {
      profiler.time('collectComputeNodes:nodeCollection');
      const effectiveElement = getEffectiveProductElementForCompute(
        element,
        path,
        enrichedElementMap,
      );
      const bp = collectProductCountBlueprint(
        effectiveElement,
        path,
        descriptorMap,
        enrichedElementMap,
      );
      if (bp) blueprints.push(bp);
      profiler.timeEnd('collectComputeNodes:nodeCollection');
    }
  });

  // Instantiate blueprints into compute nodes with closures
  let nodes: ComputeNode<number>[] = blueprints.map((bp) =>
    instantiateNode(bp, parentVariables, descriptorMap, elementVarsMap),
  );

  const ctx: ComputedVariableContext = {
    version: traversalVersion,
    elementIdsInScope,
    descriptorMap,
    elementsInScope,
    enrichedElementMap,
    nodes,
    project,
    weatherHours,
    proposal,
  };

  profiler.time('collectComputeNodes:computedVariables');
  const finalNodes = applyComputedVariables(ctx);
  profiler.timeEnd('collectComputeNodes:computedVariables');
  profiler.countAdd('collectComputeNodes:nodes', finalNodes.length);
  profiler.timeEnd('collectComputeNodes');

  return {
    nodes: finalNodes,
    enrichedElements: enrichedElementMap,
  };
};

/**
 * Apply structural enrichment to an element in memory.
 * Does NOT mutate the project — returns a new element if changed.
 * LEGACY helpers
 *
 * Steps (matching old {@link enrichElementStructure} minus redundant updateProductIdsInVariablesRecord):
 *  1. {@link updateInheritedPropertyFallbacks} — set fallbackCount from parent variables
 *  2. {@link applyElementCategoryProperties} — add/remove properties from category definition
 *  3. {@link syncDataTableProperties} — sync property values from connected data tables
 *  4. {@link autoUpdateElementQuantity} — setup auto-fallback expressions for quantities
 *
 * @remarks When pipeline profiling is enabled, each step is timed under
 * `enrichElementForCompute:*` labels (see {@link profiler}).
 */
const enrichElementForCompute = (
  element: IElement,
  path: ElementPath,
  nodeVars: ExpressionVariables,
  dataTables: IDataTable[],
): IElement => {
  let enriched: IElement = element;

  profiler.time('enrichElementForCompute:updateInheritedPropertyFallbacks');
  const parentFromPath = path.length > 0 ? path[path.length - 1] : undefined;
  const parentElement =
    parentFromPath && projectGuards.is.element(parentFromPath)
      ? parentFromPath
      : undefined;
  enriched = updateInheritedPropertyFallbacks(
    enriched,
    nodeVars,
    parentElement,
  );
  profiler.timeEnd('enrichElementForCompute:updateInheritedPropertyFallbacks');

  profiler.time('enrichElementForCompute:applyElementCategoryProperties');
  enriched = applyElementCategoryProperties(enriched);
  profiler.timeEnd('enrichElementForCompute:applyElementCategoryProperties');

  profiler.time('enrichElementForCompute:syncDataTableProperties');
  enriched = syncDataTableProperties(enriched, dataTables);
  profiler.timeEnd('enrichElementForCompute:syncDataTableProperties');

  profiler.time('enrichElementForCompute:autoUpdateElementQuantity');
  enriched = autoUpdateElementQuantity(
    enriched,
    path,
    {
      [element.id]: nodeVars,
    },
    { cacheNamespace: 'compute' },
  );
  profiler.timeEnd('enrichElementForCompute:autoUpdateElementQuantity');

  return enriched;
};

/**
 * When category enrichment adds new product element children,
 * create blueprints for them so their count gets resolved.
 */
const collectNewProductElementBlueprints = (
  originalElement: IElement,
  enrichedElement: IElement,
  path: ElementPath,
  descriptorMap: NodeDescriptorMap,
  elementIds: Set<string>,
  blueprints: NodeBlueprint[],
  enrichedElementMap: Map<string, IElement>,
): void => {
  if (enrichedElement === originalElement) return;

  const originalChildIds = new Set(
    (originalElement.elements ?? []).map((c) => c.id),
  );

  for (const child of enrichedElement.elements ?? []) {
    if (originalChildIds.has(child.id)) continue;

    if (projectGuards.is.productElement(child)) {
      elementIds.add(child.id);
      const pathWithParent = appendElementToPath(path, enrichedElement);
      const bp = collectProductCountBlueprint(
        child,
        pathWithParent,
        descriptorMap,
        enrichedElementMap,
      );
      if (bp) {
        blueprints.push(bp);
      }
    }
  }
};

/**
 * Create a {@link ComputeNode} from a {@link NodeBlueprint} by attaching a compute closure.
 * When {@link bp.varsElementId} is set, per-element vars from {@link elementVarsMap} are used
 * as the closure base (product count nodes need density, densityArray, etc.).
 */
const instantiateNode = (
  bp: NodeBlueprint,
  parentVariables: ExpressionVariables,
  descriptorMap: NodeDescriptorMap,
  elementVarsMap?: Map<string, ExpressionVariables>,
): ComputeNode<number> => {
  if (bp.kind === 'inheritance') {
    return {
      id: bp.id,
      dependencies: bp.dependencies,
      compute: (resolvedDeps) => {
        const value = bp.sourceNodeId
          ? resolvedDeps[bp.sourceNodeId]
          : undefined;
        if (typeof value === 'number') return value;
        // No ancestor source — fall back to the property's own fallbackCount (if any)
        // before defaulting to 0.
        return typeof bp.fallbackResolved === 'number'
          ? bp.fallbackResolved
          : 0;
      },
    };
  }
  const baseVars =
    bp.varsElementId && elementVarsMap
      ? { ...parentVariables, ...(elementVarsMap.get(bp.varsElementId) ?? {}) }
      : parentVariables;
  const { countExp, min, max } = bp;
  return {
    id: bp.id,
    dependencies: bp.dependencies,
    compute: (resolvedDeps) => {
      const variables: ExpressionVariables = { ...baseVars };
      for (const [depId, value] of Object.entries(resolvedDeps)) {
        const name = getVariableNameFromDescriptor(descriptorMap, depId);
        if (name !== undefined && typeof value === 'number') {
          variables[name] = value;
        }
      }
      const result = errorHandledSolve(countExp, variables, { min, max });
      return result?.resolved ?? 0;
    },
  };
};

/**
 * Update the `resolved` preview on an {@link ExpressionValue} count source.
 * Returns the same reference when already in sync so callers can skip cloning.
 * {@link createExpression} also refreshes `formatted` and preserves any user `value`.
 */
const syncResolvedCountSource = (
  source: ExpressionValue,
  resolved: number,
): ExpressionValue =>
  source.resolved === resolved
    ? source
    : createExpression({ ...source, resolved });

/**
 * Mirror the dependency-graph result onto a property's active count source.
 * Matches {@link getCount}'s precedence: an explicit `count` wins over `fallbackCount`.
 */
const syncPropertyResolvedValue = (
  prop: IElementExpressionProperty,
  resolved: number,
): IElementExpressionProperty => {
  if (isExpressionValue(prop.count)) {
    const count = syncResolvedCountSource(prop.count, resolved);
    return count === prop.count ? prop : { ...prop, count };
  }
  if (isExpressionValue(prop.fallbackCount)) {
    const fallbackCount = syncResolvedCountSource(prop.fallbackCount, resolved);
    return fallbackCount === prop.fallbackCount
      ? prop
      : { ...prop, fallbackCount };
  }
  return prop;
};

/**
 * Sync resolved node values back into an element's expression properties.
 *
 * The enrich phase ({@link enrichElementForCompute}) produces preview `resolved`
 * values for scope-aware expressions like `getSBEFLifetime(sbef_code)` before the
 * `sbef_code` dependency is in scope, so an enriched element can carry a wrong
 * lifetime (e.g. building-default 50 instead of the stored 20). The dependency graph
 * resolves the correct value, but it only lands in {@link resolvedValues}.
 *
 * Since {@link buildResultsRecordFromProposalComputeRecord} reads lifetime/coverage
 * from the enriched tree — and {@link enrichedElementToStructuralChanges} persists
 * `fallbackCount` diffs back to the project — the enriched element must reflect the
 * resolved value, otherwise B4 is dropped and CO2 collapses on re-enrich (DEV-3421).
 */
const syncEnrichedElementResolvedValues = (
  element: IElement,
  resolvedValues: Record<string, number>,
): IElement => {
  let changed = false;
  const nextProperties = getElementProperties(element).map((prop) => {
    if (!isElementExpressionProperty(prop)) return prop;
    // Only reconcile the SBEF lifetime expression, whose enrich-time preview is unreliable
    // (getSBEFLifetime resolves to a building-default before sbef_code is in scope) and which
    // buildResultsRecord reads from the enriched tree for B4. Other expressions keep their
    // enrich value so product masses/counts (A-phase) don't shift on re-enrich.
    const activeSource = prop.count ?? prop.fallbackCount;
    if (
      !isExpressionValue(activeSource) ||
      !(activeSource.expression ?? '').includes('getSBEFLifetime')
    ) {
      return prop;
    }
    const resolved = resolvedValues[NODE_ID.property(element.id, prop.name)];
    if (typeof resolved !== 'number') return prop;
    const synced = syncPropertyResolvedValue(prop, resolved);
    if (synced !== prop) changed = true;
    return synced;
  });
  return changed ? { ...element, properties: nextProperties } : element;
};

/**
 * {@link syncEnrichedElementResolvedValues} on an element and all nested {@link IElement} children.
 * Needed when a parent row is already in {@link enrichedElements} but carries stale child refs
 * (e.g. schablon parent + recipe child lifetime) — DEV-3421.
 */
const syncEnrichedElementTreeResolvedValues = (
  element: IElement,
  resolvedValues: Record<string, number>,
): IElement => {
  let synced = syncEnrichedElementResolvedValues(element, resolvedValues);
  let childrenChanged = false;
  const nextChildren = synced.elements.map((child) => {
    if (!projectGuards.is.element(child)) return child;
    const nextChild = syncEnrichedElementTreeResolvedValues(
      child,
      resolvedValues,
    );
    if (nextChild !== child) childrenChanged = true;
    return nextChild;
  });
  if (childrenChanged) synced = { ...synced, elements: nextChildren };
  return synced;
};

const PROPERTY_NODE_ID_SEPARATOR = ':prop:';

/**
 * Parse a property compute node id (`elementId:prop:name`) back to its parts.
 */
const parsePropertyNodeId = (
  nodeId: string,
): { elementId: string; name: string } | undefined => {
  const separatorIndex = nodeId.indexOf(PROPERTY_NODE_ID_SEPARATOR);
  if (separatorIndex < 0) return undefined;
  return {
    elementId: nodeId.slice(0, separatorIndex),
    name: nodeId.slice(separatorIndex + PROPERTY_NODE_ID_SEPARATOR.length),
  };
};

/**
 * Apply {@link syncEnrichedElementResolvedValues} to every enriched element so the
 * stored enriched tree stays consistent with the resolved dependency graph.
 *
 * Also patches elements that were not structurally enriched (same reference after
 * enrich) but still need lifetime sync for resultsRecord rollups (DEV-3421).
 */
const syncEnrichedElementsWithResolvedValues = (
  enrichedElements: Map<string, IElement>,
  resolvedValues: Record<string, number>,
  version: Baseline,
): Map<string, IElement> => {
  const synced = new Map<string, IElement>();
  for (const [id, element] of enrichedElements) {
    synced.set(
      id,
      syncEnrichedElementTreeResolvedValues(element, resolvedValues),
    );
  }

  const pathIndex = buildElementPathIndex(version);
  for (const [nodeId, resolved] of Object.entries(resolvedValues)) {
    if (typeof resolved !== 'number') continue;
    const parsed = parsePropertyNodeId(nodeId);
    if (!parsed || parsed.name !== ElementPropertyName.Lifetime) continue;
    if (synced.has(parsed.elementId)) continue;

    const indices = pathIndex.get(parsed.elementId);
    if (indices === undefined) continue;
    const element = getElementByPath(version, indices);
    if (!element || !projectGuards.is.element(element)) continue;

    synced.set(
      parsed.elementId,
      syncEnrichedElementTreeResolvedValues(element, resolvedValues),
    );
  }

  return synced;
};

/**
 * Replace nested child refs in enriched parents with their synced rows from the map.
 * Parents are snapshotted before children are enriched, so a separately synced recipe
 * child would otherwise stay stale inside the parent subtree (DEV-3421).
 */
const hoistSyncedChildrenInEnrichedElements = (
  synced: Map<string, IElement>,
): Map<string, IElement> => {
  const next = new Map(synced);
  let changed = false;
  for (const [id, element] of synced) {
    let childrenChanged = false;
    const elements = element.elements.map((child) => {
      if (!projectGuards.is.element(child)) return child;
      const syncedChild = next.get(child.id);
      if (syncedChild && syncedChild !== child) {
        childrenChanged = true;
        return syncedChild;
      }
      return child;
    });
    if (childrenChanged) {
      next.set(id, { ...element, elements });
      changed = true;
    }
  }
  return changed ? next : synced;
};

/**
 * Build expression variables for an element, inheriting from parent.
 * When productRecord is available, includes product-derived vars (density, etc.).
 * Stores result in varsMap for children to inherit from.
 */
const resolveElementVariables = (
  element: OneOfElements,
  path: ElementPath,
  baseVariables: ExpressionVariables,
  varsMap: Map<string, ExpressionVariables>,
  productRecord?: ProductRecord,
  dataTables: IDataTable[] = [],
): ExpressionVariables => {
  profiler.count('resolveElementVariables:calls');
  // Inherit from nearest ancestor or fall back to base variables
  const parentElement = getLast(path);
  const parentVars = parentElement
    ? (varsMap.get(parentElement.id) ?? baseVariables)
    : baseVariables;

  // When product record is available, build full per-element variables
  // (includes density, densityArray, getProductDensity, category mappings, etc.)
  const elementVars = productRecord
    ? getElementExpressionVariables(
        element,
        parentVars,
        productRecord,
        dataTables,
      )
    : parentVars;

  varsMap.set(element.id, elementVars);
  return elementVars;
};

/**
 * Generate a ProposalComputeRecord from resolved values for a single proposal
 * @param version - Building version
 * @param resolvedValues - Resolved expression values
 * @param products - Product lookup
 * @param proposal - Proposal for filtering elements and tagging the record
 * @param marketEstimates - Project snapshot for percentile-based product overrides (same as legacy results path)
 */
export const generateProposalComputeRecord = (
  version: Baseline,
  resolvedValues: Record<string, number>,
  products: ProductRecord = genericProductsLookup,
  proposal: IProposal,
  enrichedElements?: Map<string, IElement>,
  marketEstimates?: ProjectMarketEstimates,
): ProposalComputeRecord => {
  const baseElements = getComputeElements(version);

  // Include new children (e.g., product elements added by category enrichment)
  const additionalElements = collectNewChildElements(
    baseElements,
    enrichedElements,
  );
  const elements = [...baseElements, ...additionalElements];

  const items: Record<IElementID, ComputeItem> = {};
  const proposalId = proposal.id;

  for (const element of elements) {
    if (isTemporaryElementId(element.id)) continue;
    const effectiveElement = enrichedElements?.get(element.id) ?? element;
    const properties: Record<string, number> = {};
    let results: Results = {};
    let count: number | undefined;
    let productId: string | undefined;

    if (projectGuards.is.element(element)) {
      // Use enriched element if available (has correct properties from category/data tables)
      const props = getElementAndQuantityProperties(effectiveElement);
      for (const prop of props) {
        if (isExpressionLikeProperty(prop)) {
          const nodeId = NODE_ID.property(element.id, prop.name);
          const value = resolvedValues[nodeId];
          if (value !== undefined) {
            properties[prop.name] = value;
          }
        }
      }
    } else if (projectGuards.is.productElement(effectiveElement)) {
      // Per-element unit + overrides (DEV-2917); do not use raw product factors × count
      const countNodeId = NODE_ID.productCount(effectiveElement.id);
      count = resolvedValues[countNodeId];
      productId = effectiveElement.product_id;

      if (count !== undefined && productId) {
        const product = products[productId];
        if (product) {
          const factors = getProductElementConversionFactors(
            products,
            effectiveElement,
            product,
            marketEstimates,
          );
          // Store the conservative A1–A3 / A5 deltas (generic data × (ConservativeDataConversionFactor
          // - 1), EPD unchanged) in parallel so the result-scope toggle can add the uplift without
          // recomputing (DEV-3411). Parent and version rollups carry them via sumConversionFactors.
          // A user-set carbon slider override counts as a specific value → no conservative uplift.
          const conservativeFactor =
            isGenericProductElement(effectiveElement) &&
            !hasCarbonLevelOverride(effectiveElement)
              ? getProductConservativeFactor(product)
              : NEUTRAL_CONSERVATIVE_FACTOR;
          results = withConservativeCarbon(
            multiplyConversionFactors(factors, count),
            { conservativeFactor },
          );
        }
      }
    }

    // Create compute item
    const item: ComputeItem = {
      id: element.id,
      proposalId,
      properties,
      results,
      productId,
      count,
      dependencies: [], // Could be filled from the dependency graph if needed
    };

    items[element.id] = item;
  }

  // Convert enriched elements map to record for serialization
  const enrichedElementEntries =
    enrichedElements && enrichedElements.size > 0
      ? [...enrichedElements.entries()].filter(
          ([id]) => !isTemporaryElementId(id),
        )
      : [];
  const enrichedElementRecord: Record<IElementID, IElement> | undefined =
    enrichedElementEntries.length > 0
      ? Object.fromEntries(enrichedElementEntries)
      : undefined;

  return {
    proposalId,
    versionId: version.id,
    hash: generateVersionHash(version),
    items,
    enrichedElements: enrichedElementRecord,
  };
};

export interface ComputeVersionDependencies {
  /**
   * Data tables to use for the compute.
   */
  dataTables?: IDataTable[];

  /**
   * Product record to use for the compute.
   * If not provided, the generic products lookup will be used.
   */
  products?: ProductRecord;

  /**
   * Weather hours (8760) for heat demand record; when provided, post_heating_demand is computed from resolved envelope.
   */
  weatherHours?: EnvironmentHourData[];
}

/**
 * Compute all values for a building version
 * This is the main entry point for the compute phase
 *
 * @param project - The project containing the version
 * @param version - The building version to compute
 * @returns ProposalComputeRecord with all resolved values for the active proposal
 */
export const computeVersion = (
  project: Project,
  version: Baseline,
  dependencies?: ComputeVersionDependencies,
): ProposalComputeRecord => {
  return computeVersionForProposal(
    project,
    version,
    createBaselineComputeProposal(version),
    dependencies,
  );
};

const createBaselineComputeProposal = (version: Baseline): IProposal => ({
  id: version.id,
  name: version.name,
  colorIndex: 0,
  actionIds: [],
  resultsRecord: version.resultsRecord,
});

/**
 * Compute all values for a building version with a specific proposal.
 * Builds project-level expression variables (GFA, storeys, activity, etc.)
 * and per-element product-derived variables (density, etc.) for accurate resolution.
 *
 * Structural enrichment (category props, data tables, auto quantities) is computed
 * in-memory during node collection and included in the returned record.
 *
 * @param project - The project containing the version
 * @param version - The building version to compute
 * @param proposal - The proposal to compute for
 * @param dataTables - External data tables for property sync
 * @returns ProposalComputeRecord with resolved values for the proposal
 */
export const computeVersionForProposal = (
  project: Project,
  version: Baseline,
  proposal: IProposal,
  dependencies: ComputeVersionDependencies = {},
): ProposalComputeRecord => {
  profiler.time('computeVersionForProposal');
  profiler.count('computeVersionForProposal:calls');
  const {
    products = { ...genericProductsLookup, ...version.products },
    dataTables = [],
    weatherHours = [],
  } = dependencies;

  const meta = getProjectMeta(project);
  const design = getBuildingDesignFromProjectOrBuilding(project);
  let parentVariables = getBaseParentVariablesForCompute(project, products);

  // Per-proposal energy variables (heating_demand_space etc.) so heat_demand_record differs per proposal
  const activities = getEnergyActivitiesFromDesign(design);
  const heatedArea = getHeatedAreaFromEnergyActivities(activities);
  parentVariables = {
    ...parentVariables,
    ...getEnergyExpressionVariables(
      version,
      meta,
      weatherHours,
      heatedArea,
      design,
      proposal,
    ),
  };

  // Collect compute nodes + structurally enriched elements (project + weatherHours for heat demand record)
  const { nodes, enrichedElements } = collectComputeNodes(
    version,
    parentVariables as ExpressionVariables,
    proposal,
    products,
    dataTables,
    project,
    weatherHours,
  );

  // Resolve all expressions — relaxed mode handles quantity cycles
  // (e.g. mass ↔ density) by using product-derived seed values from parentVariables
  profiler.time('computeVersionForProposal:resolveNodes');
  const resolvedValues = resolveNodesRelaxed(nodes);
  profiler.timeEnd('computeVersionForProposal:resolveNodes');

  // Reconcile enrich-time previews (e.g. lifetime) with the resolved graph so the
  // enriched tree used for results + persistence is correct (DEV-3421).
  const syncedEnrichedElements = hoistSyncedChildrenInEnrichedElements(
    syncEnrichedElementsWithResolvedValues(
      enrichedElements,
      resolvedValues,
      version,
    ),
  );

  const record = generateProposalComputeRecord(
    version,
    resolvedValues,
    products,
    proposal,
    syncedEnrichedElements,
    project.marketEstimates,
  );
  profiler.timeEnd('computeVersionForProposal');
  return record;
};

/**
 * Compute all values for each proposal. Each proposal runs {@link computeVersionForProposal}
 * so category enrichment (e.g. product slots from heating type) sees that proposal's actions.
 *
 * @param project - The project containing the version
 * @param version - Baseline building version (proposal actions merged inside {@link collectComputeNodes})
 * @param proposals - Proposals to compute
 * @param dependencies - Products, data tables, weather hours
 */
export const computeVersionForAllProposals = (
  project: Project,
  version: Baseline,
  proposals: IProposal[],
  dependencies: ComputeVersionDependencies = {},
): Map<ProposalId, ProposalComputeRecord> => {
  profiler.time('computeVersionForAllProposals');
  const results = new Map<ProposalId, ProposalComputeRecord>();

  for (const proposal of proposals) {
    profiler.time('computeVersionForAllProposals:perProposal');
    results.set(
      proposal.id,
      computeVersionForProposal(project, version, proposal, dependencies),
    );
    profiler.timeEnd('computeVersionForAllProposals:perProposal');
  }

  profiler.timeEnd('computeVersionForAllProposals');
  return results;
};

/**
 * Compute values for all proposals in a version.
 * Delegates to {@link computeVersionForAllProposals}.
 */
export const computeAllProposals = (
  project: Project,
  version: Baseline,
  dependencies?: ComputeVersionDependencies,
): Map<ProposalId, ProposalComputeRecord> =>
  computeVersionForAllProposals(
    project,
    version,
    getProposalsInVersion(version),
    dependencies,
  );

/**
 * Get the baseline ProposalComputeRecord from a pre-computed map.
 *
 * @param version - The building version
 * @param proposalRecords - Pre-computed proposal records
 * @param project - Optional project for fallback computation
 * @returns ProposalComputeRecord for the active proposal
 */
export const getBaselineProposalRecord = (
  version: Baseline,
  proposalRecords: Map<ProposalId, ProposalComputeRecord>,
  project?: Project,
): ProposalComputeRecord | undefined => {
  const baselineProposal = createBaselineComputeProposal(version);
  const existing = proposalRecords.get(baselineProposal.id);
  if (existing) return existing;

  // Fallback: compute if not cached and project is provided
  if (project) {
    const computed = computeVersionForProposal(
      project,
      version,
      baselineProposal,
    );
    proposalRecords.set(baselineProposal.id, computed);
    return computed;
  }

  return undefined;
};

/**
 * Compute all values for a project (all versions)
 * Each version gets one ProposalComputeRecord for its active proposal
 *
 * @param project - The project to compute
 * @returns Map of version ID to ProposalComputeRecord
 */
export const computeProject = (
  project: Project,
): Map<string, ProposalComputeRecord> => {
  const results = new Map<string, ProposalComputeRecord>();

  for (const version of project.baselines) {
    results.set(version.id, computeVersion(project, version));
  }

  return results;
};
